import { type RefObject, useEffect } from "react";

/**
 * Trap keyboard focus within `ref` while `active`, and restore focus to the
 * previously-focused element on deactivate. Closes the WCAG 2.4.3 gap our
 * hand-rolled modals had (Tab used to escape into the page behind). Also moves
 * initial focus into the container.
 */
export function useFocusTrap(
	ref: RefObject<HTMLElement | null>,
	active: boolean,
): void {
	useEffect(() => {
		if (!active) return;
		const el = ref.current;
		if (!el) return;

		const previouslyFocused = document.activeElement as HTMLElement | null;

		const selector =
			'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

		const focusables = () =>
			Array.from(el.querySelectorAll<HTMLElement>(selector)).filter(
				(n) => n.offsetParent !== null || n === document.activeElement,
			);

		// Move focus into the dialog (first focusable, else the container itself).
		const first = focusables()[0];
		if (first) first.focus();
		else el.focus();

		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key !== "Tab") return;
			const nodes = focusables();
			if (nodes.length === 0) {
				e.preventDefault();
				return;
			}
			const firstNode = nodes[0]!;
			const lastNode = nodes[nodes.length - 1]!;
			if (e.shiftKey && document.activeElement === firstNode) {
				e.preventDefault();
				lastNode.focus();
			} else if (!e.shiftKey && document.activeElement === lastNode) {
				e.preventDefault();
				firstNode.focus();
			}
		};

		el.addEventListener("keydown", onKeyDown);
		return () => {
			el.removeEventListener("keydown", onKeyDown);
			previouslyFocused?.focus?.();
		};
	}, [ref, active]);
}
